/* eslint-disable no-unused-vars */
import { Platform } from 'react-native';
import { EnvVars } from '../constants/EnvConfig';

type SentrySDK = typeof import('@sentry/react') | typeof import('@sentry/react-native');

let Sentry: SentrySDK;
if (Platform.OS === 'web') {
  Sentry = require('@sentry/react');
} else {
  Sentry = require('@sentry/react-native');
}

const FgRed = '\x1b[31m';
const FgYellow = '\x1b[33m';
const FgWhite = '\x1b[37m';
const FgCyan = '\x1b[36m';

const getPrefix = () => `${new Date().toISOString()} [ptf]`;

function prefix(message: string): string {
  return `${getPrefix()} ${message}`;
}

type SentryLogLevel = 'debug' | 'info' | 'warn' | 'error';

// Severity ranks for the Sentry forwarding threshold (higher number = more severe). Values mirror
// Sentry's own log severity numbers (SEVERITY_TEXT_TO_SEVERITY_NUMBER in @sentry/core, itself the
// OpenTelemetry severity numbering). That constant isn't exported from Sentry's public API, so we
// restate the ones we use rather than import an internal module.
const SENTRY_LEVEL_RANK: Record<SentryLogLevel, number> = {
  debug: 5,
  info: 9,
  warn: 13,
  error: 17,
};

// Fallback floor when an environment omits/misconfigures minSentryLogLevel: forward only
// warn and above, so a misconfig never silently ships verbose logs from a user's device.
const DEFAULT_MIN_SENTRY_LOG_LEVEL: SentryLogLevel = 'warn';

/**
 * Logger class with dependency injection for Sentry and config
 */
export class Logger {
  private sentry: SentrySDK;

  private config: typeof EnvVars;

  /**
   * Creates a logger instance
   * @param {SentrySDK} sentry - Sentry instance
   * @param {typeof EnvVars} config - Environment configuration
   */
  constructor(sentry: SentrySDK, config: typeof EnvVars) {
    this.sentry = sentry;
    this.config = config;
  }

  /**
   * Whether a structured log at the given level should be forwarded to Sentry, based on
   * `useSentry` and the configured `minSentryLogLevel` floor. Exceptions (error) report
   * independently via `captureException` and are intentionally not gated here.
   * @param {SentryLogLevel} level - The level of the log being considered
   * @returns {boolean} True if the log should reach Sentry's structured Logs
   */
  private shouldForwardToSentry(level: SentryLogLevel): boolean {
    if (!this.config.useSentry) return false;
    const floor =
      SENTRY_LEVEL_RANK[this.config.minSentryLogLevel as SentryLogLevel] ??
      SENTRY_LEVEL_RANK[DEFAULT_MIN_SENTRY_LOG_LEVEL];
    return SENTRY_LEVEL_RANK[level] >= floor;
  }

  /**
   * Logs an error to Sentry and/or the console, depending on the environment.
   * If the error is instance of an `Error` (or subclass), Sentry is sent
   * the error's _enumerable_ properties as context
   * @param {unknown} error - The error to log
   * @param {unknown} details - Optional additional details to include in Sentry context
   * @returns {string | null} Event ID produced by Sentry
   */
  error(error: unknown, details: Record<string, unknown> | undefined = undefined): string | null {
    if (this.config.consoleLog) {
      // eslint-disable-next-line no-console
      console.error(FgRed, getPrefix(), error, details);
    }

    if (!this.config.useSentry) {
      return null;
    }

    try {
      // see: https://docs.sentry.io/platforms/javascript/usage/#capturing-errors
      const errorDetail = error instanceof Error ? { ...error, ...details } : details ?? {};
      return this.sentry.captureException(error, { contexts: { errorDetail } });
    } catch (logErrorError) {
      // captureException itself failed; use Sentry's structured logger to report failure
      try {
        this.sentry.logger.error(prefix('An error could not be reported to Sentry'), {
          error: logErrorError,
        });
      } catch {
        // Sentry is fully unreachable; give up silently.
      }
      return null;
    }
  }

  /**
   * Logs a message with optional details to the console and forwards to Sentry's structured logger.
   * @param {string} message - The message to log
   * @param {unknown} details - Optional details to include
   */
  info(message: string, details: Record<string, unknown> | undefined = undefined): void {
    const formattedMessage = prefix(message);
    if (this.config.consoleLog) {
      if (details !== undefined) {
        // eslint-disable-next-line no-console
        console.log(FgWhite, formattedMessage, details);
      } else {
        // eslint-disable-next-line no-console
        console.log(FgWhite, formattedMessage);
      }
    }

    if (!this.shouldForwardToSentry('info')) return;
    try {
      this.sentry.logger.info(message, details);
    } catch {
      // Logging is best-effort; never let it throw.
    }
  }

  /**
   * Logs a warning with optional details to the console and forwards to Sentry's structured logger.
   * @param {string} message - The message to log
   * @param {unknown} details - Optional details to include
   */
  warn(message: string, details: Record<string, unknown> | undefined = undefined): void {
    const formattedMessage = prefix(message);
    if (this.config.consoleLog) {
      if (details !== undefined) {
        // eslint-disable-next-line no-console
        console.warn(FgYellow, formattedMessage, details);
      } else {
        // eslint-disable-next-line no-console
        console.warn(FgYellow, formattedMessage);
      }
    }

    if (!this.shouldForwardToSentry('warn')) return;
    try {
      this.sentry.logger.warn(message, details);
    } catch {
      // Logging is best-effort; never let it throw.
    }
  }

  /**
   * Logs a debug message to the console and forwards to Sentry's structured logger.
   * @param {string} message - The message to log
   * @param {unknown} details - Optional details to include
   */
  debug(message: string, details: Record<string, unknown> | undefined = undefined): void {
    const formattedMessage = prefix(message);
    if (this.config.debugLog) {
      if (details !== undefined) {
        // eslint-disable-next-line no-console
        console.debug(FgCyan, formattedMessage, details);
      } else {
        // eslint-disable-next-line no-console
        console.debug(FgCyan, formattedMessage);
      }
    }

    if (!this.shouldForwardToSentry('debug')) return;
    try {
      this.sentry.logger.debug(message, details);
    } catch {
      // Logging is best-effort; never let it throw.
    }
  }
}

// Default singleton instance for application use
const logger = new Logger(Sentry, EnvVars);

// Convenience exports for backward compatibility
// Bind methods to preserve 'this' context when called standalone
export const logError = logger.error.bind(logger);
export const logInfo = logger.info.bind(logger);
export const log = logger.info.bind(logger);
export const debug = logger.debug.bind(logger);
